import { useEffect, useMemo, useRef, useState } from "react";
import { api, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { MessageSquare, Send, Trash2, AtSign } from "lucide-react";
import { toast } from "sonner";

export default function ProjectComments({ projectId }) {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [users, setUsers] = useState([]);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [mentionState, setMentionState] = useState(null); // { query, position }
  const textareaRef = useRef(null);

  const load = () => {
    api.get(`/projects/${projectId}/comments`).then((r) => setComments(r.data || [])).catch(() => {});
  };
  useEffect(() => {
    load();
    api.get("/users").then((r) => setUsers(r.data || [])).catch(() => setUsers([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // Detect "@<query>" right before the caret and surface user suggestions
  const onBodyChange = (e) => {
    const value = e.target.value;
    setBody(value);
    const caret = e.target.selectionStart ?? value.length;
    const before = value.slice(0, caret);
    const match = before.match(/(?:^|\s)@([A-Za-z0-9._-]*)$/);
    if (match) {
      setMentionState({ query: match[1].toLowerCase(), tokenStart: caret - match[1].length - 1 });
    } else {
      setMentionState(null);
    }
  };

  const suggestions = useMemo(() => {
    if (!mentionState) return [];
    const q = mentionState.query;
    return users
      .filter((u) => {
        const hay = `${u.name || ""} ${u.email || ""}`.toLowerCase();
        return q === "" || hay.includes(q);
      })
      .slice(0, 6);
  }, [users, mentionState]);

  const insertMention = (u) => {
    if (!textareaRef.current || !mentionState) return;
    const v = body;
    const before = v.slice(0, mentionState.tokenStart);
    const after = v.slice(textareaRef.current.selectionStart ?? v.length);
    const handle = (u.name || u.email || "").replace(/\s+/g, "_");
    const next = `${before}@${handle} ${after}`;
    setBody(next);
    setMentionState(null);
    setTimeout(() => {
      textareaRef.current?.focus();
      const pos = (before + `@${handle} `).length;
      textareaRef.current?.setSelectionRange(pos, pos);
    }, 0);
  };

  // Extract mentioned user IDs from body by matching tokens to known users
  const extractMentionedIds = (text) => {
    const tokens = Array.from(text.matchAll(/(?:^|\s)@([A-Za-z0-9._-]+)/g)).map((m) => m[1].toLowerCase());
    if (!tokens.length) return [];
    const ids = new Set();
    users.forEach((u) => {
      const handle = (u.name || u.email || "").replace(/\s+/g, "_").toLowerCase();
      const emailUser = (u.email || "").split("@")[0].toLowerCase();
      if (tokens.includes(handle) || tokens.includes(emailUser)) ids.add(u.id);
    });
    return Array.from(ids);
  };

  const submit = async () => {
    const text = body.trim();
    if (!text) return;
    setBusy(true);
    try {
      const mentioned_user_ids = extractMentionedIds(text);
      const { data } = await api.post(`/projects/${projectId}/comments`, { body: text, mentioned_user_ids });
      setComments((prev) => [...prev, data]);
      setBody("");
      setMentionState(null);
      toast.success(mentioned_user_ids.length ? `Comment posted · ${mentioned_user_ids.length} mention${mentioned_user_ids.length === 1 ? "" : "s"}` : "Comment posted");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to post comment");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (cid) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await api.delete(`/projects/${projectId}/comments/${cid}`);
      setComments((prev) => prev.filter((c) => c.id !== cid));
      toast.success("Deleted");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete");
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      submit();
    }
  };

  return (
    <div className="flat-card p-6" data-testid="project-comments-card">
      <div className="flex items-center justify-between mb-3">
        <div className="label-eyebrow flex items-center gap-2">
          <MessageSquare className="w-3.5 h-3.5" /> Comments
        </div>
        <span className="text-[10px] uppercase tracking-widest font-bold text-[#4B5563]">
          {comments.length} comment{comments.length === 1 ? "" : "s"}
        </span>
      </div>

      <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1 mb-4">
        {comments.length === 0 ? (
          <div className="text-xs text-[#9CA3AF]">No comments yet. Be the first to share an update.</div>
        ) : (
          comments.map((c) => (
            <CommentItem key={c.id} c={c} canDelete={user?.role === "admin" || c.by_id === user?.id} onDelete={() => remove(c.id)} />
          ))
        )}
      </div>

      <div className="relative">
        <Textarea
          ref={textareaRef}
          value={body}
          onChange={onBodyChange}
          onKeyDown={onKeyDown}
          rows={3}
          placeholder="Write a comment… use @ to tag a teammate. Cmd/Ctrl + Enter to send."
          className="rounded-none resize-none text-sm"
          data-testid="comment-input"
        />
        {mentionState && suggestions.length > 0 && (
          <div className="absolute left-0 right-0 sm:right-auto sm:min-w-[280px] bottom-full mb-1 border border-[#E5E7EB] bg-white shadow-lg z-20 max-h-56 overflow-y-auto" data-testid="mention-suggestions">
            {suggestions.map((u) => (
              <button
                key={u.id}
                type="button"
                onClick={() => insertMention(u)}
                className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-[#F9FAFB] text-sm"
                data-testid={`mention-suggestion-${u.id}`}
              >
                <AtSign className="w-3.5 h-3.5 text-[#9CA3AF]" />
                <span className="font-medium">{u.name || u.email}</span>
                <span className="text-[10px] uppercase tracking-widest text-[#9CA3AF] ml-auto">{u.role}</span>
              </button>
            ))}
          </div>
        )}
        <div className="flex items-center justify-between mt-2">
          <div className="text-[10px] text-[#9CA3AF]">
            Type <span className="font-mono bg-[#F3F4F6] px-1">@</span> to mention a teammate
          </div>
          <Button
            type="button"
            disabled={busy || !body.trim()}
            onClick={submit}
            size="sm"
            className="rounded-none h-9 px-4"
            data-testid="comment-submit"
          >
            <Send className="w-3.5 h-3.5 mr-1.5" />
            {busy ? "Posting…" : "Post comment"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function CommentItem({ c, canDelete, onDelete }) {
  const segments = useMemo(() => splitBodyIntoSegments(c.body, c.mentions || []), [c.body, c.mentions]);
  return (
    <div className="border-l-2 border-[#E5E7EB] pl-3 py-1 group" data-testid={`comment-${c.id}`}>
      <div className="flex items-baseline justify-between gap-2">
        <div className="text-xs">
          <span className="font-semibold text-[#0a0a0a]">{c.by_name}</span>
          <span className="text-[10px] uppercase tracking-widest text-[#9CA3AF] ml-2">{c.by_role}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-[#9CA3AF] whitespace-nowrap">{fmtDateTime(c.created_at)}</span>
          {canDelete && (
            <button
              onClick={onDelete}
              className="opacity-0 group-hover:opacity-100 text-[#9CA3AF] hover:text-[#EF4444] transition"
              data-testid={`comment-delete-${c.id}`}
              aria-label="Delete comment"
            >
              <Trash2 className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>
      <div className="text-sm text-[#1F2937] mt-1 whitespace-pre-wrap break-words">
        {segments.map((seg, i) =>
          seg.type === "mention" ? (
            <span
              key={i}
              className={seg.known
                ? "bg-[#E0E7FF] text-[#002FA7] px-1 py-0.5 font-semibold"
                : "text-[#4B5563]"}
            >
              @{seg.handle}
            </span>
          ) : (
            seg.text
          )
        )}
      </div>
    </div>
  );
}

// Split comment body into plain-text and mention segments. React handles all
// escaping during render, so no dangerouslySetInnerHTML is needed.
function splitBodyIntoSegments(text, mentions) {
  const known = new Set();
  (mentions || []).forEach((m) => {
    const handle = (m.name || "").replace(/\s+/g, "_");
    if (handle) known.add(handle.toLowerCase());
  });
  const body = text || "";
  const re = /(^|\s)@([A-Za-z0-9._-]+)/g;
  const out = [];
  let cursor = 0;
  let match;
  while ((match = re.exec(body)) !== null) {
    const [full, lead, handle] = match;
    const start = match.index + lead.length;  // position of '@'
    if (start > cursor) out.push({ type: "text", text: body.slice(cursor, start) });
    out.push({ type: "mention", handle, known: known.has(handle.toLowerCase()) });
    cursor = match.index + full.length;
  }
  if (cursor < body.length) out.push({ type: "text", text: body.slice(cursor) });
  return out;
}

function fmtDateTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const dDay = new Date(d); dDay.setHours(0, 0, 0, 0);
  const time = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  if (dDay.getTime() === today.getTime()) return `Today ${time}`;
  return `${fmtDate(iso)} ${time}`;
}
